import { Buffer } from 'redux-saga';
import { actionChannel, call, put, select, take } from 'redux-saga/effects';

import { User } from 'entities/user';
import { State } from 'store';
import { GenericAction, IClient, IGetActions, ISelectors, RequestWithId, Types } from '../genericAPIGatewayService';

export enum Service {
  Users = 'Users:Users',
}

export function* get(
  client: IClient<User>,
  actions: IGetActions<User>,
  getUser: (state: State, id: string) => User,
  id: string,
) {
  // If user is already loaded, ignore
  const userExists = yield select(getUser, id);
  if (!!userExists) {
    return;
  }

  try {
    const user = yield call([client, 'get'], `/users/${id}`);
    // Need to use id here because the user's sub is returned as the
    // id from the users service.
    return yield put(actions.completeGet({ ...user, id }));
  } catch (error) {
    return yield put(actions.failGet(id, (error as Error).message));
  }
}

export const userRequestBuffer = (): Buffer<RequestWithId<Service.Users>> => {
  let buffer: { [id: string]: RequestWithId<Service.Users> } = {};

  return {
    isEmpty: () => Object.keys(buffer).length === 0,
    put: (request: RequestWithId<Service.Users>) => (buffer[request.payload.id] = request),
    take: () => {
      const keys = Object.keys(buffer);
      if (keys.length === 0) {
        return undefined;
      }

      const action = { ...buffer[keys[0]] };
      delete buffer[keys[0]];
      return action;
    },
    flush: () => (buffer = {}),
  };
};

export function customGetSaga(client: IClient<User>, actions: IGetActions<User>, selectors: ISelectors<User>) {
  return function* saga() {
    const isRequest = (action: GenericAction<User, Service.Users>) =>
      action.type === Types.GET_REQUESTED && action.service === Service.Users;
    const requestChannel = yield actionChannel(isRequest as any, userRequestBuffer());
    while (true) {
      const action: RequestWithId<Service.Users> = yield take(requestChannel);
      yield call(get, client, actions, selectors.getEntity, action.payload.id);
    }
  };
}
